import { z } from "zod";
import type { AcCardStatus } from "./accreditation";
import type { AccreditationOrgRef } from "./accreditation-records";
import { PAGE_SIZE_MAX, searchParam } from "./pagination";
import type { TransportDisplayStatus } from "./transport";
import {
	type EntryPointRef,
	type Flight,
	type FlightStatus,
	type FlightSummary,
	TRAVEL_DIRECTIONS,
	TRAVEL_DIRECTION_SEGMENTS,
	TRAVEL_GROUP_KEY_MAX,
	TRAVEL_GROUP_RANGE_DAYS_MAX,
	TRAVEL_MODES,
	TRAVEL_READINESS_ATOMS,
	type TravelDirection,
	type TravelFormMessages,
	type TravelMode,
	type TravelReadinessAtom,
	enTravelFormMessages,
	flightStatusListParam,
	travelDateField,
	travelDaysBetween,
} from "./travel";

/**
 * M9b — Arrival & Departure on Audrey's layout ("her layout, our data").
 * Plan: `~/.claude/plans/m9b-ad-layout/PLAN.md` §2. Wire shapes only
 * (snake_case).
 *
 * A JOURNEY is what M9 called a group: a flight (`f:<flight_id>`), or a land
 * / sea service at one hour (`s:<entry_point_id>:<mode>:<slot>:<SERVICE>`) —
 * computed, never stored, keyed by `group_key` (opaque; it does not depend on
 * the date range, so it can sit in a URL).
 *
 * VIEW (CLAUDE.md, Transport — NOC privacy): `view: "full"` for admins and an
 * OPERATION session, `view: "noc"` for ORGANIZATION. Decided by the ROLE,
 * never by a permission (bug 7). The NOC shapes are SEPARATE types, built from
 * the caller's own people only: no `contingents`, no `shared`, no SHORT
 * transfer, no ref of a host trip, no actor in history.
 *
 * READINESS (one rule, `notReadySql(view)` in the API; `journeyReadiness()` /
 * `isNotReady()` below are its display-only mirror, pinned by unit tests): an
 * ARRIVAL leg still to come (`expected_at` ≥ now, or no time yet) is NOT
 * READY when its card is not APPROVED, or it needs transport with no live
 * transfer that has a CONFIRMED driver (none / no driver yet, or a driver the
 * delay cascade flagged for a re-check), or its card has no open bed.
 */

// ── Vocabulary and limits ───────────────────────────────────────────────────

/** `GET /travel/journeys?direction=` — BOTH is the Overview's day board. */
export const JOURNEY_DIRECTIONS = [...TRAVEL_DIRECTIONS, "BOTH"] as const;
export type JourneyDirection = (typeof JOURNEY_DIRECTIONS)[number];

/**
 * Journey list orders (keyset over the materialised, range-bounded set):
 *   next         still to come first (expected ascending), then the past ones (expected descending)
 *   time_asc     expected time ascending (journeys with no time last)
 *   time_desc    expected time descending (journeys with no time last)
 *   people_desc  most people first, then expected ascending
 * Ties break on (direction, group_key).
 */
export const JOURNEY_SORTS = ["next", "time_asc", "time_desc", "people_desc"] as const;
export type JourneySort = (typeof JOURNEY_SORTS)[number];
export const JOURNEY_SORT_DEFAULT: JourneySort = "next";

/** `not_ready=1` = any atom; an atom keeps the journeys with at least one leg failing it. */
export const JOURNEY_NOT_READY_FILTERS = ["1", ...TRAVEL_READINESS_ATOMS] as const;
export type JourneyNotReadyFilter = (typeof JOURNEY_NOT_READY_FILTERS)[number];

/** `not_ready` reads compute readiness for EVERY leg in range before paging: a shorter range than the list's. */
export const TRAVEL_NOT_READY_RANGE_DAYS_MAX = 2;
/** `GET /travel/journeys` default page size (max PAGE_SIZE_MAX). */
export const TRAVEL_JOURNEYS_PAGE_SIZE_DEFAULT = 25;
/** Trips named on a journey's detail; the rest are counted in `trips_more`. */
export const JOURNEY_TRIPS_MAX = 5;
/** `GET /flights/:id/activity` default page size (max PAGE_SIZE_MAX). */
export const FLIGHT_ACTIVITY_PAGE_SIZE_DEFAULT = 20;

/**
 * A transfer's display status as journeys count it (`TransportDisplayStatus`
 * folded): PENDING → pending, SHORT → short (full view only; the NOC view
 * shows SHORT as assigned), ASSIGNED → assigned, EN_ROUTE / PARTIAL →
 * en_route, COMPLETED → done. `none` = needs transport, no live transfer.
 * `recheck` = ASSIGNED, but the delay cascade flagged the trip
 * (`needs_recheck` / `needs_redispatch`): the driver is not confirmed — it
 * is never counted as assigned (the `recheck` readiness atom).
 * A leg that does not need transport counts in none of them.
 */
export const JOURNEY_TRANSFER_STATES = ["none", "pending", "short", "recheck", "assigned", "en_route", "done"] as const;
export type JourneyTransferState = (typeof JOURNEY_TRANSFER_STATES)[number];

/** The transfer statuses that count as "has a driver" for readiness (a SHORT trip may not seat this person). */
export const TRANSFER_READY_STATUSES = ["ASSIGNED", "EN_ROUTE", "PARTIAL", "COMPLETED"] as const satisfies readonly TransportDisplayStatus[];

/**
 * The transfer statuses a delay-cascade flag (`needs_recheck`,
 * `needs_redispatch`) puts in doubt: a driver is assigned but not yet on the
 * road. Once a vehicle is EN_ROUTE the driver is evidently confirmed; a
 * PENDING / SHORT trip already fails the `transfer` atom.
 */
export const TRANSFER_RECHECK_STATUSES = ["ASSIGNED"] as const satisfies readonly TransportDisplayStatus[];

/** The journey-level status `status=` filters on: the flight's, or for a service SCHEDULED / DELAYED (a manual delay). */
export type JourneyStatus = FlightStatus;

// ── Messages ────────────────────────────────────────────────────────────────

export interface TravelJourneysMessages extends TravelFormMessages {
	/** `not_ready` with a direction other than ARRIVAL. */
	notReadyArrivalsOnly?: string;
	/** `not_ready` over more than TRAVEL_NOT_READY_RANGE_DAYS_MAX days. */
	notReadyRangeTooLong?: string;
}

export const enTravelJourneysMessages: Required<Pick<TravelJourneysMessages, "notReadyArrivalsOnly" | "notReadyRangeTooLong">> & TravelJourneysMessages = {
	...enTravelFormMessages,
	notReadyArrivalsOnly: "Readiness is for arrivals only",
	notReadyRangeTooLong: `At most ${TRAVEL_NOT_READY_RANGE_DAYS_MAX} days when looking for journeys that are not ready`,
};

type M = TravelJourneysMessages;

// ── Query schemas ───────────────────────────────────────────────────────────

const uuid = z.string().uuid();
/** `1` / `true` → true, `0` / `false` → false — never `z.coerce.boolean()` (bug 2). */
const flagParam = z.enum(["1", "0", "true", "false"]).transform((v) => v === "1" || v === "true");
const cursorParam = z.string().min(1).max(400);

/**
 * `GET /travel/journeys` — the journeys in scope in a range (≤
 * TRAVEL_GROUP_RANGE_DAYS_MAX days, `to` defaults to `from`), ONE statement,
 * keyset (`cursor` opaque: send back `next_cursor` exactly as received).
 * `total`, `people_total`, `status_counts` (and `shared_count`, full view)
 * come with the first page only.
 *   status     flight statuses (comma list), at journey level
 *   q          a flight, service no., origin, or a person's name / card number on it
 *   late       journeys with `delayed_count` > 0
 *   not_ready  `1` (any atom) or an atom; ARRIVAL only, ≤ TRAVEL_NOT_READY_RANGE_DAYS_MAX days
 *   at_risk    journeys with `connection_at_risk_count` > 0
 *   manual     flight journeys whose tracking is MANUAL
 *   shared     (full view only — 422 for a NOC) journeys with more than one contingent
 */
export function makeTravelJourneysQuery(m: M = enTravelJourneysMessages) {
	return z
		.object({
			games_id: uuid,
			direction: z.enum(JOURNEY_DIRECTIONS).default("ARRIVAL"),
			from: travelDateField(m),
			to: travelDateField(m).optional(),
			mode: z.enum(TRAVEL_MODES).optional(),
			entry_point_id: uuid.optional(),
			responsible_organization_id: uuid.optional(),
			status: flightStatusListParam.optional(),
			q: searchParam.optional(),
			late: flagParam.optional(),
			not_ready: z.enum(JOURNEY_NOT_READY_FILTERS).optional(),
			at_risk: flagParam.optional(),
			manual: flagParam.optional(),
			shared: flagParam.optional(),
			sort: z.enum(JOURNEY_SORTS).default(JOURNEY_SORT_DEFAULT),
			cursor: cursorParam.optional(),
			limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(TRAVEL_JOURNEYS_PAGE_SIZE_DEFAULT),
		})
		.superRefine((v, ctx) => {
			const days = v.to === undefined ? 0 : travelDaysBetween(v.from, v.to);
			if (days < 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.rangeInvalid });
			else if (days + 1 > TRAVEL_GROUP_RANGE_DAYS_MAX) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.rangeTooLong });
			else if (v.not_ready !== undefined && days + 1 > TRAVEL_NOT_READY_RANGE_DAYS_MAX)
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: m.notReadyRangeTooLong ?? enTravelJourneysMessages.notReadyRangeTooLong });
			if (v.not_ready !== undefined && v.direction !== "ARRIVAL")
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["not_ready"], message: m.notReadyArrivalsOnly ?? enTravelJourneysMessages.notReadyArrivalsOnly });
		});
}
export const travelJourneysQuery = makeTravelJourneysQuery();
export type TravelJourneysQuery = z.output<typeof travelJourneysQuery>;
export type TravelJourneysQueryInput = z.input<typeof travelJourneysQuery>;

/** `/travel/journeys/:direction/:key` — `direction` is the path segment (`arrival` | `departure`) and comes out upper-case; `key` is a `group_key`. */
export const travelJourneyParams = z.object({
	direction: z.enum([TRAVEL_DIRECTION_SEGMENTS.ARRIVAL, TRAVEL_DIRECTION_SEGMENTS.DEPARTURE]).transform((s): TravelDirection => (s === "arrival" ? "ARRIVAL" : "DEPARTURE")),
	key: z.string().trim().min(1).max(TRAVEL_GROUP_KEY_MAX),
});
export type TravelJourneyParams = z.output<typeof travelJourneyParams>;

/** `GET /travel/journeys/:direction/:key?games_id=` — one journey, ONE statement; 404 when the caller sees none of its legs. */
export const travelJourneyQuery = z.object({ games_id: uuid });
export type TravelJourneyQuery = z.output<typeof travelJourneyQuery>;

/** `GET /travel/attention?games_id=&date=` — the board day and the next; `date` defaults to today (games' zone). */
export const travelAttentionQuery = z.object({
	games_id: uuid,
	date: travelDateField(enTravelFormMessages).optional(),
});
export type TravelAttentionQuery = z.output<typeof travelAttentionQuery>;

/** `GET /travel/by-organization?games_id=` — not a list: one row per organisation in scope (bounded by the catalogue). */
export const travelByOrganizationQuery = z.object({ games_id: uuid });
export type TravelByOrganizationQuery = z.output<typeof travelByOrganizationQuery>;

/** `GET /travel/day-counts` — the widest range one call may count (the day chips show a week; paging moves a week). */
export const TRAVEL_DAY_COUNTS_RANGE_DAYS_MAX = 14;

/**
 * `GET /travel/day-counts?games_id=&from=&to=` — journeys and people per
 * local day (games' zone) and direction, for the day chips' badges. `to`
 * defaults to `from`; ≤ TRAVEL_DAY_COUNTS_RANGE_DAYS_MAX days. ONE statement
 * over the same scoped scan as `/travel/journeys`, so a day's count equals
 * that day's journeys list `total` / `people_total`.
 */
export const travelDayCountsQuery = z
	.object({
		games_id: uuid,
		from: travelDateField(enTravelFormMessages),
		to: travelDateField(enTravelFormMessages).optional(),
	})
	.superRefine((v, ctx) => {
		const days = v.to === undefined ? 0 : travelDaysBetween(v.from, v.to);
		if (days < 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: enTravelFormMessages.rangeInvalid });
		else if (days + 1 > TRAVEL_DAY_COUNTS_RANGE_DAYS_MAX)
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: `At most ${TRAVEL_DAY_COUNTS_RANGE_DAYS_MAX} days` });
	});
export type TravelDayCountsQuery = z.output<typeof travelDayCountsQuery>;

/** `GET /flights/:id/activity` — keyset, newest first, on `(occurred_at, audit_log_id)`. */
export const flightActivityQuery = z.object({
	cursor: cursorParam.optional(),
	limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(FLIGHT_ACTIVITY_PAGE_SIZE_DEFAULT),
});
export type FlightActivityQuery = z.output<typeof flightActivityQuery>;

// ── Journeys: list ──────────────────────────────────────────────────────────

/** Transfer counts of a journey / part, full view. Keys are JOURNEY_TRANSFER_STATES (lower-case: `ok()` keeps them). */
export interface JourneyTransferCounts {
	none: number;
	pending: number;
	short: number;
	/** ASSIGNED with the driver to re-check (never counted in `assigned`). */
	recheck: number;
	assigned: number;
	en_route: number;
	done: number;
}

/** NOC view: no SHORT (a short trip shows as assigned — SHORT would reveal other people). */
export type JourneyNocTransferCounts = Omit<JourneyTransferCounts, "short">;

/** The readiness counts every journey, part and detail carries. */
interface JourneyCounts {
	/** Legs the caller can see. */
	people: number;
	/** On APPROVED cards. */
	approved: number;
	needs_transport: number;
	/** Cards with an open bed (ARRIVAL: a bed to go to; DEPARTURE: still holding one). */
	beds: number;
	/** Of those, checked in. */
	checked_in: number;
	/** DEPARTURE: no open bed and a closed one (checked out). 0 for arrivals. */
	checked_out: number;
	/** ARRIVAL legs still to come that are not ready (`notReadySql`). 0 for departures. */
	not_ready: number;
}

/** One contingent on a journey, full view. */
export interface JourneyPart extends JourneyCounts {
	responsible_organization: AccreditationOrgRef;
	transfer: JourneyTransferCounts;
}

/** One contingent on a journey, NOC view (its own organisation only). */
export interface JourneyNocPart extends JourneyCounts {
	responsible_organization: AccreditationOrgRef;
	transfer: JourneyNocTransferCounts;
}

/** What every view of a journey says about the trip itself. */
interface JourneyHead extends JourneyCounts {
	/** Opaque; the detail route's `:key`, and `/travel/legs?group=`. */
	group_key: string;
	direction: TravelDirection;
	mode: TravelMode;
	flight: FlightSummary | null;
	entry_point: EntryPointRef;
	service_no: string | null;
	/** As `TravelGroup.origin`: the other end of the trip. */
	origin: string | null;
	travel_date: string;
	/** Earliest among the journey's legs. */
	scheduled_at: string | null;
	expected_at: string | null;
	/** Earliest and latest `expected_at` among its legs (a delay can split them). */
	expected_from: string | null;
	expected_to: string | null;
	/** The flight's status; for a service SCHEDULED, or DELAYED when a leg carries a manual delay. */
	status: JourneyStatus;
	delayed_count: number;
	connection_at_risk_count: number;
	/**
	 * The journey's transit paths, one per distinct chain of connecting
	 * flights among its legs (people on one main flight can connect
	 * differently), most people first, at most JOURNEY_CONNECTIONS_MAX;
	 * `[]` when every leg is direct. Counts are the caller's own people (NOC
	 * view: its own legs only, like every count here).
	 */
	connections: JourneyConnection[];
	/** Paths beyond JOURNEY_CONNECTIONS_MAX, not listed. */
	connections_more: number;
}

/** A journey head lists at most this many transit paths (`connections`); the rest are counted in `connections_more`. */
export const JOURNEY_CONNECTIONS_MAX = 4;

/**
 * One transit path of a journey (`JourneyHead.connections`): the legs whose
 * connecting flights are exactly the same flights, in the same order. The
 * chain is the leg's flights in TRAVEL order (contracts `legFlightChain`):
 * ARRIVAL = connections then the main flight, DEPARTURE = the main flight then
 * connections. Instants are ISO 8601 UTC; zones are IANA (`airports`), null
 * when the airport is unknown.
 */
export interface JourneyConnection {
	/** Where they change, in travel order: the destination of every flight of the chain but the last (1 = "1 stop · SIN"). */
	via_iata: string[];
	/** Legs (people) on this path. */
	people: number;
	/** Where the chain starts: the first flight's origin and expected take-off (ARRIVAL: the first connection; DEPARTURE: the main flight). */
	first_origin_iata: string | null;
	first_departure_at: string | null;
	first_origin_time_zone: string | null;
	/** Where the chain ends: the last flight's destination and expected landing (ARRIVAL: the main flight; DEPARTURE: the last connection). */
	last_destination_iata: string | null;
	last_arrival_at: string | null;
	last_destination_time_zone: string | null;
	/** The shortest change on expected times (later take-off − earlier landing), whole minutes, negative = overlapping; null when no change has both times. */
	min_change_minutes: number | null;
	/** The leg-level `connection_at_risk` (`isConnectionAtRisk`) — the same for every leg of the path. */
	at_risk: boolean;
}

/** One row of `GET /travel/journeys`, full view (admins, OPERATION). */
export interface JourneyFull extends JourneyHead {
	view: "full";
	/** Distinct organisations on it (> 1 = "shared"). */
	contingents: number;
	transfer: JourneyTransferCounts;
	/** One per contingent, by size. */
	parts: JourneyPart[];
}

/** One row of `GET /travel/journeys`, NOC view (ORGANIZATION): its own people only, no contingent count. */
export interface JourneyNoc extends JourneyHead {
	view: "noc";
	transfer: JourneyNocTransferCounts;
	parts: JourneyNocPart[];
}

export type Journey = JourneyFull | JourneyNoc;

/** `status_counts`: statuses that occur only, as values (never a Record keyed by an enum — `ok()` lower-cases keys). */
export type JourneyStatusCount = { status: JourneyStatus; count: number };

interface JourneysPageHead {
	next_cursor: string | null;
	/** First page only (no cursor), else null: journeys / people matching every filter. */
	total: number | null;
	people_total: number | null;
	/** First page only, else null: the chips' counts over every filter BUT `status`. */
	status_counts: JourneyStatusCount[] | null;
}

export interface JourneysPageFull extends JourneysPageHead {
	view: "full";
	items: JourneyFull[];
	/** First page only, else null: journeys with more than one contingent. */
	shared_count: number | null;
}

export interface JourneysPageNoc extends JourneysPageHead {
	view: "noc";
	items: JourneyNoc[];
}

/** `GET /travel/journeys`. `page.view` tells the shapes apart. */
export type JourneysPage = JourneysPageFull | JourneysPageNoc;

// ── Journeys: detail ────────────────────────────────────────────────────────

/** A trip carrying people of the journey, full view (the dispatch desk may open it). */
export interface JourneyTrip {
	transport_request_id: string;
	/** `TRIP-000123`. */
	ref: string;
	status: TransportDisplayStatus;
	/** ASSIGNED with the driver to re-check (`TravelLegTransfer.recheck`). */
	recheck: boolean;
	/** The journey's people on it (the caller's scope). */
	people: number;
}

/** NOC view: its OWN trips only, named; host trips are only counted (`host_trips`). */
export interface JourneyNocTrip {
	transport_request_id: string;
	ref: string;
	status: Exclude<TransportDisplayStatus, "SHORT">;
	/** As the full view: its driver is being confirmed. */
	recheck: boolean;
	people: number;
}

/**
 * The passenger filter chips' counts: the legs FAILING each readiness atom
 * (`journeyReadiness`, whatever their time) — each equals the total of
 * `GET /travel/legs?group=<key>&readiness=<atom>` (one SQL builder).
 */
export interface JourneyChipCounts {
	/** `readiness=card`: card not APPROVED. */
	not_approved: number;
	/** `readiness=bed`: ARRIVAL with no open bed (0 for a departure). */
	no_bed: number;
	/** `readiness=transfer`: needs transport and has no live transfer WITH A DRIVER (none, PENDING, SHORT in the full view). */
	no_transfer: number;
	/** `readiness=recheck`: needs transport, its trip is ASSIGNED but flagged — the driver is to be re-checked. */
	driver_recheck: number;
}

interface JourneyDetailHead extends JourneyHead {
	/** The whole flight row (`latest` times, tracking), or null for a service. */
	flight: Flight | null;
	chips: JourneyChipCounts;
	/** People on it who also have the other leg booked ("Departure booked: n of N"). */
	other_leg_count: number;
	/** ARRIVAL: expected + the entry point's `dwell_minutes`; null for departures or with no time. */
	pickup_at: string | null;
}

/** `GET /travel/journeys/:direction/:key`, full view. */
export interface JourneyDetailFull extends JourneyDetailHead {
	view: "full";
	contingents: number;
	transfer: JourneyTransferCounts;
	parts: JourneyPart[];
	/** Live trips carrying its people, not-yet-driven first, at most JOURNEY_TRIPS_MAX. */
	trips: JourneyTrip[];
	trips_more: number;
	/** The flight's latest audit row; null for a service or a flight never written by a person or the tracker. */
	latest_update: { reason: string | null; occurred_at: string; actor_name: string | null } | null;
}

/** `GET /travel/journeys/:direction/:key`, NOC view. */
export interface JourneyDetailNoc extends JourneyDetailHead {
	view: "noc";
	transfer: JourneyNocTransferCounts;
	parts: JourneyNocPart[];
	trips: JourneyNocTrip[];
	trips_more: number;
	/** Host trips carrying its people: a count, never a ref. */
	host_trips: number;
	/** As the full view, without the actor. */
	latest_update: { reason: string | null; occurred_at: string } | null;
}

export type JourneyDetail = JourneyDetailFull | JourneyDetailNoc;

// ── Attention, by organisation, flight activity ─────────────────────────────

interface TravelAttentionHead {
	/** The board day; the window is it and the next day. */
	date: string;
	/** ARRIVAL legs still to come that are not ready, and the journeys holding them. */
	not_ready: { people: number; journeys: number };
	/** ARRIVAL legs still to come whose card is not APPROVED (the `card` atom: = the people of `/travel/journeys?not_ready=card` over the window). */
	not_approved: { people: number };
	/** People on CANCELLED or DIVERTED flights, and those flights. */
	cancelled: { people: number; flights: number };
	/** People whose connection is at risk. */
	at_risk: { people: number };
}

/** `GET /travel/attention`, full view. */
export interface TravelAttentionFull extends TravelAttentionHead {
	view: "full";
	/** Flights of the window set by hand (tracking MANUAL). */
	manual_flights: number;
	/** Entry points with an hour of `date` whose people exceed `desk_capacity_per_hour`. */
	over_capacity: { entry_points: number };
}

/** `GET /travel/attention`, NOC view: its own people only; no manual flights, no capacity. */
export interface TravelAttentionNoc extends TravelAttentionHead {
	view: "noc";
}

export type TravelAttention = TravelAttentionFull | TravelAttentionNoc;

/**
 * One row of `GET /travel/by-organization`. The standing cards in scope
 * (not REJECTED / WITHDRAWN) partition into
 * `people = arrived + booked + not_approved + no_arrival`:
 *   arrived       APPROVED, arrival leg landed (flight LANDED, or the expected time has passed)
 *   booked        APPROVED, arrival leg still to come
 *   not_approved  not APPROVED yet (with or without an arrival leg)
 *   no_arrival    APPROVED, no arrival leg (their sum = the summary's `approved_without_arrival`)
 */
export interface ByOrganizationRow {
	responsible_organization: AccreditationOrgRef;
	people: number;
	arrived: number;
	booked: number;
	not_approved: number;
	no_arrival: number;
	/** Arrival legs per mode, modes that occur only (values, not keys). */
	modes: Array<{ mode: TravelMode; people: number }>;
}

/** Journeys and people of one direction on one day. */
export interface TravelDayDirectionCount {
	journeys: number;
	people: number;
}

/**
 * One day of `GET /travel/day-counts`. One shape for every view: a NOC's
 * counts are over its own people only (the scan's scope), and nothing here
 * names or counts another contingent.
 */
export interface TravelDayCount {
	/** YYYY-MM-DD, the games' zone. */
	date: string;
	arrivals: TravelDayDirectionCount;
	departures: TravelDayDirectionCount;
}

/** `GET /travel/day-counts` — only the days with any travel in scope, ascending. */
export interface TravelDayCountsList {
	days: TravelDayCount[];
}

/** `GET /travel/by-organization` — sorted `no_arrival` DESC, `people` DESC. */
export interface TravelByOrganization {
	items: ByOrganizationRow[];
}

/** One write to a flight (`audit_logs`, `entity_table = 'flights'`). */
export interface FlightActivityItem {
	audit_log_id: string;
	occurred_at: string;
	action: "INSERT" | "UPDATE" | "DELETE";
	reason: string | null;
	/** Changed columns, bookkeeping excluded. */
	changed_fields: string[];
	changes: Record<string, { from: unknown; to: unknown }>;
	/** Null in the NOC view. */
	actor: { type: "USER" | "AI_AGENT" | "SYSTEM" | "MIGRATION"; name: string | null } | null;
}

/** `GET /flights/:id/activity` — 404 unless the caller sees ≥ 1 leg on the flight. */
export interface FlightActivityPage {
	view: "full" | "noc";
	items: FlightActivityItem[];
	next_cursor: string | null;
}

// ── Readiness: display-only mirror of `notReadySql` ─────────────────────────

/** What readiness reads of one leg. */
export interface JourneyReadinessInput {
	direction: TravelDirection;
	expected_at: string | null;
	card_status: AcCardStatus;
	needs_transport: boolean;
	/** The leg's live transfer's display status, or null when it has none. */
	transfer_status: TransportDisplayStatus | null;
	/** The live transfer is flagged by the delay cascade (`needs_recheck` OR `needs_redispatch`); false when it has none. */
	transfer_flagged: boolean;
	/** An open room assignment for the card. */
	has_bed: boolean;
}

/** Per atom: true = fine. */
export type JourneyReadiness = Record<TravelReadinessAtom, boolean>;

/** A live transfer whose driver is to be re-checked: flagged while it reads TRANSFER_RECHECK_STATUSES (`transferRecheckSql` in the API). */
export function isTransferRecheck(status: TransportDisplayStatus | null, flagged: boolean): boolean {
	return flagged && status !== null && (TRANSFER_RECHECK_STATUSES as readonly string[]).includes(status);
}

/**
 * The atoms of one leg: `card` = APPROVED; `transfer` = needs no transport,
 * or its live transfer has a driver (TRANSFER_READY_STATUSES); `recheck` =
 * needs no transport, or that driver is not flagged for a re-check
 * (`isTransferRecheck`); `bed` = an open bed. Direction-blind: `isNotReady`
 * decides whom it applies to.
 */
export function journeyReadiness(leg: Omit<JourneyReadinessInput, "direction" | "expected_at">): JourneyReadiness {
	return {
		card: leg.card_status === "APPROVED",
		transfer: !leg.needs_transport || (leg.transfer_status !== null && (TRANSFER_READY_STATUSES as readonly string[]).includes(leg.transfer_status)),
		recheck: !leg.needs_transport || !isTransferRecheck(leg.transfer_status, leg.transfer_flagged),
		bed: leg.has_bed,
	};
}

/**
 * An ARRIVAL leg still to come (`expected_at` ≥ `now`, or no time yet) with
 * any atom failing — or, with `atom`, that atom failing. Departures and past
 * arrivals are never "not ready".
 */
export function isNotReady(leg: JourneyReadinessInput, now: Date | number = Date.now(), atom?: TravelReadinessAtom): boolean {
	if (leg.direction !== "ARRIVAL") return false;
	if (leg.expected_at !== null && Date.parse(leg.expected_at) < +now) return false;
	const r = journeyReadiness(leg);
	return atom ? !r[atom] : !(r.card && r.transfer && r.recheck && r.bed);
}

/**
 * The journey-transfer bucket of a display status (see JOURNEY_TRANSFER_STATES);
 * `view: "noc"` folds SHORT into assigned; a flagged ASSIGNED trip
 * (`isTransferRecheck`) is `recheck`, never assigned.
 */
export function journeyTransferState(status: TransportDisplayStatus | null, view: "full" | "noc" = "full", flagged = false): JourneyTransferState | null {
	switch (status) {
		case null:
			return "none";
		case "PENDING":
			return "pending";
		case "SHORT":
			return view === "noc" ? (flagged ? "recheck" : "assigned") : "short";
		case "ASSIGNED":
			return isTransferRecheck(status, flagged) ? "recheck" : "assigned";
		case "EN_ROUTE":
		case "PARTIAL":
			return "en_route";
		case "COMPLETED":
			return "done";
		default:
			// CANCELLED / REJECTED: not a live transfer.
			return null;
	}
}
